import axios from 'axios'
import { AuthService } from '../services'
import { APIPath, CommonConstant } from '../constant'
import { BrowserUtility } from 'utility'

const onSuccess = response => {
  return response.data
}

const onError = async error => {
  if (error?.status === 401) {
    AuthService.logout()
  }
  return Promise.reject({
    error: error?.response?.data?.error || error?.response?.data,
    status: error?.response?.status,
  })
}

const request = async (options, isSecure) => {
  const headers = {}

  if (isSecure) {
    const token = AuthService.getToken()
    headers.Authorization = `Bearer ${token}`
  }

  headers.app = 'EAuditor'
  headers['Access-Control-Allow-Origin'] = '*'

  const client = axios.create({
    baseURL: APIPath.server,
    headers: { ...headers },
  })

  try {
    // Make the request
    return await client(options).then(onSuccess)
  } catch (error) {
    // Check if the error is due to token expiration
    if (error.response?.status === 401 && isSecure) {
      try {
        const RefreshToken = BrowserUtility.getObj('user')?.refreshToken
        const newToken = await AuthService.refreshToken({
          refreshToken: RefreshToken,
        })
        if (!newToken) {
          throw new Error('Failed to refresh token')
        }

        headers['X-TOKEN'] = newToken?.accessToken
        headers.Authorization = `Bearer ${newToken?.accessToken}`

        BrowserUtility.saveObj('token', newToken?.accessToken)
        const userObj = {
          ...newToken,
        }
        BrowserUtility.saveObj('user', userObj)

        // Create a new axios client with updated headers
        const retryClient = axios.create({
          baseURL: APIPath.server,
          headers: { ...headers },
        })

        return await retryClient(options).then(onSuccess).catch(onError)
      } catch (refreshError) {
        AuthService.logout()
        throw refreshError
      }
    } else {
      // Handle other errors
      throw error
    }
  }
}

const uploadFiles = (url, data, headers = {}) => {
  headers['Access-Control-Allow-Origin'] = '*'
  const client = axios({
    url,
    method: 'PUT',
    headers: {
      ...headers,
    },
    data,
  })

  return client.then(onSuccess).catch(onError)
}

export class BaseService {
  static get(url, isSecure = true) {
    return request(
      {
        url,
        method: 'GET',
      },
      isSecure,
    )
  }

  static post(url, data, isSecure = true) {
    return request(
      {
        url,
        method: 'POST',
        data,
      },
      isSecure,
    )
  }

  static put(url, data, isSecure = true) {
    return request(
      {
        url,
        method: 'PUT',
        data,
      },
      isSecure,
    )
  }

  static patch(url, data, isSecure = true) {
    return request(
      {
        url,
        method: 'PATCH',
        data,
      },
      isSecure,
    )
  }

  static moralisAPI(url) {
    const client = axios.create({
      baseURL: APIPath.moralisAPI,
    })
    return client({
      url,
      method: 'GET',
    })
      .then(onSuccess)
      .catch(onError)
  }

  static openseaAPI(url) {
    const headers = {}

    if (CommonConstant.mode === 'PRODUCTION') {
      headers['X-API-KEY'] = process.env.REACT_APP_OPENSEA_API_KEY
    }
    const client = axios.create({
      baseURL: APIPath.openSeaAPI,
      headers,
    })
    return client({
      url,
      method: 'GET',
    })
      .then(onSuccess)
      .catch(onError)
  }

  static extenralAPICall(url) {
    const client = axios({
      url,
      method: 'GET',
      timeout: 1000 * 3,
    })
    return client.then(onSuccess).catch(onError)
  }

  static remove(url, isSecure = true) {
    return request(
      {
        url,
        method: 'DELETE',
      },
      isSecure,
    )
  }

  static upload = (url, data, header) => {
    return uploadFiles(url, data, header)
  }
}
